// src/stores/contentStore.js
import { create } from 'zustand';
import contentService from '../services/contentService';
import useNotificationStore from './notificationStore';

const useContentStore = create((set, get) => ({
  // State
  content: [],
  totalContent: 0,
  currentContent: null,
  contentCalendar: {},
  isLoading: false,
  criticalError: null,  // For blocking errors (e.g., failed to load content list)
  operationError: null, // For non-blocking errors (e.g., failed to update content)
  filters: {
    status: 'all',
    type: 'all',
    brandId: 'all',
    category: 'all'
  },
  pagination: {
    page: 1,
    limit: 10,
    totalPages: 0
  },
  selectedDate: new Date(),
  
  // Actions for managing state
  setFilters: (filters) => {
    set({ 
      filters: { ...get().filters, ...filters },
      pagination: { ...get().pagination, page: 1 } // Reset to page 1 when filters change
    });
  },
  uploadContentAssets: async (contentId, files) => {
    try {
      set({ isLoading: true, operationError: null });
      
      // Use the upload service to upload files
      const uploadPromises = files.map(file => {
        // Create FormData object for each file
        const formData = new FormData();
        formData.append('file', file);
        
        // Get the correct file MIME type
        let fileMetadata = {};
        if (file.type.includes('image/')) {
          fileMetadata.type = 'image';
        } else if (file.type.includes('video/')) {
          fileMetadata.type = 'video';
        } else if (file.type.includes('audio/')) {
          fileMetadata.type = 'audio';
        } else {
          fileMetadata.type = 'document';
        }
        
        // Add metadata
        Object.entries(fileMetadata).forEach(([key, value]) => {
          formData.append(key, value);
        });
        
        return contentService.uploadContentAsset(contentId, formData);
      });
      
      const results = await Promise.all(uploadPromises);
      
      // Refresh content if it's the current one
      if (get().currentContent?._id === contentId) {
        await get().fetchContentById(contentId);
      }
      
      set({ isLoading: false });
      return results.map(result => result.data.asset);
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  setPagination: (pagination) => set({ 
    pagination: { ...get().pagination, ...pagination } 
  }),
  
  setSelectedDate: (date) => set({ selectedDate: date }),
  
  clearError: () => set({ criticalError: null, operationError: null }),
  clearCriticalError: () => set({ criticalError: null }),
  clearOperationError: () => set({ operationError: null }),
  
  clearCurrentContent: () => set({ currentContent: null }),
  
  // API Actions
 // Fetch content with pagination
 fetchContent: async (params) => {
  set({ isLoading: true, operationError: null });
  
  try {
    // Combine filters with params
    const queryParams = { ...params };
    
    // Include active filters
    const { filters } = get();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== 'all' && value !== '') {
        queryParams[key] = value;
      }
    });
    
    const response = await contentService.getContent(queryParams);
    
    set({ 
      content: response.data.content,
      totalContent: response.meta.total,
      pagination: {
        page: response.meta.currentPage,
        limit: response.meta.perPage,
        totalPages: response.meta.totalPages
      },
      isLoading: false 
    });
    
    return response;
  } catch (error) {
    set({ 
      isLoading: false 
    });
    
    // Show error notification
    const { showError } = useNotificationStore.getState();
    showError(error.message || 'Failed to fetch content');
    
    // Don't throw - return empty data
    return { content: [], totalContent: 0 };
  }
},
  
  fetchContentById: async (id) => {
    try {
      set({ isLoading: true, criticalError: null });
      const response = await contentService.getContentById(id);
      set({ currentContent: response.data.content, isLoading: false, criticalError: null });
      return response.data.content;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch content details';
      set({ 
        isLoading: false,
        criticalError: errorMessage
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError(errorMessage);
      
      // Don't throw - return null
      return null;
    }
  },
  
  createContent: async (data) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.createContent(data);
      set({ isLoading: false });
      return response.data.content;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ 
        isLoading: false, 
        operationError: errorMessage
      });
      // Show notification
      useNotificationStore.getState().showError(errorMessage);
      throw error;
    }
  },
  
  updateContent: async (id, data) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.updateContent(id, data);
      
      // Update in list if exists
      const updatedContent = get().content.map(item => 
        item._id === id ? response.data.content : item
      );
      
      set({ 
        content: updatedContent,
        isLoading: false,
        // Update current content if it's the one being edited
        ...(get().currentContent?._id === id 
          ? { currentContent: response.data.content } 
          : {})
      });
      
      return response.data.content;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  deleteContent: async (id) => {
    try {
      set({ isLoading: true, operationError: null });
      await contentService.deleteContent(id);
      
      // Remove from content array
      const updatedContent = get().content.filter(item => item._id !== id);
      
      set({ 
        content: updatedContent, 
        totalContent: Math.max(0, get().totalContent - 1),
        isLoading: false,
        // Reset current content if it's the one being deleted
        ...(get().currentContent?._id === id ? { currentContent: null } : {})
      });
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  uploadContentAssets: async (contentId, files) => {
    try {
      set({ isLoading: true, operationError: null });
      
      const uploadPromises = files.map(file => {
        const formData = new FormData();
        formData.append('file', file);
        return contentService.uploadContentAsset(contentId, formData);
      });
      
      const results = await Promise.all(uploadPromises);
      
      // Refresh content if it's the current one
      if (get().currentContent?._id === contentId) {
        await get().fetchContentById(contentId);
      }
      
      set({ isLoading: false });
      return results;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  fetchContentCalendar: async (params = {}) => {
    try {
      set({ isLoading: true, operationError: null });
      
      // Handle date range parameters
      const dateRange = params.dateRange || {};
      const queryParams = {
        ...params,
        startDate: dateRange.start ? dateRange.start.toISOString() : undefined,
        endDate: dateRange.end ? dateRange.end.toISOString() : undefined
      };
      
      // Remove nested dateRange object
      if (queryParams.dateRange) {
        delete queryParams.dateRange;
      }
      
      const response = await contentService.getContentCalendar(queryParams);
      
      set({ 
        contentCalendar: response.data.calendar || {},
        isLoading: false 
      });
      
      return response.data.calendar;
    } catch (error) {
      set({ 
        isLoading: false, 
        contentCalendar: {} // Reset to empty object on error
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError(error.response?.data?.message || error.message || 'Failed to fetch content calendar');
      
      // Don't throw - return empty calendar
      return {};
    }
  },
  
  initApprovalWorkflow: async (contentId, steps) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.initApprovalWorkflow(contentId, { steps });
      
      // Update current content if it's the one being updated
      if (get().currentContent?._id === contentId) {
        set({ currentContent: response.data.content });
      }
      
      set({ isLoading: false });
      return response.data.content;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  approveContent: async (contentId, notes = '') => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.approveContent(contentId, { notes });
      
      // Update content in list if exists
      const updatedContent = get().content.map(item => 
        item._id === contentId 
          ? { ...item, status: response.data.content.status }
          : item
      );
      
      set({ 
        content: updatedContent,
        isLoading: false,
        // Update current content if it's the one being approved
        ...(get().currentContent?._id === contentId 
          ? { currentContent: response.data.content }
          : {})
      });
      
      return response.data.content;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  rejectContent: async (contentId, notes = '') => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.rejectContent(contentId, { notes });
      
      // Update content in list if exists
      const updatedContent = get().content.map(item => 
        item._id === contentId 
          ? { ...item, status: response.data.content.status }
          : item
      );
      
      set({ 
        content: updatedContent,
        isLoading: false,
        // Update current content if it's the one being rejected
        ...(get().currentContent?._id === contentId 
          ? { currentContent: response.data.content }
          : {})
      });
      
      return response.data.content;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  scheduleContent: async (contentId, distributions) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.scheduleContent(contentId, { distributions });
      
      // Update content in list if exists
      const updatedContent = get().content.map(item => 
        item._id === contentId 
          ? { ...item, status: 'scheduled', distributions: response.data.content.distributions }
          : item
      );
      
      set({ 
        content: updatedContent,
        isLoading: false,
        // Update current content if it's the one being scheduled
        ...(get().currentContent?._id === contentId 
          ? { currentContent: response.data.content }
          : {})
      });
      
      return response.data.content;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  publishContent: async (contentId, platform, publishingResponse) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.publishContent(contentId, { 
        platform, 
        publishingResponse 
      });
      
      // Update content in list if exists
      const updatedContent = get().content.map(item => 
        item._id === contentId 
          ? { ...item, status: 'published' }
          : item
      );
      
      set({ 
        content: updatedContent,
        isLoading: false,
        // Update current content if it's the one being published
        ...(get().currentContent?._id === contentId 
          ? { currentContent: response.data.content }
          : {})
      });
      
      return response.data.content;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  getContentAnalytics: async (contentId, params = {}) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.getContentAnalytics(contentId, params);
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  getContentComments: async (contentId) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.getContentComments(contentId);
      set({ isLoading: false });
      return response.data.comments;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  addContentComment: async (contentId, text, isInternal = false) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.addContentComment(contentId, { 
        text, 
        isInternal 
      });
      
      // Update current content if needed
      if (get().currentContent?._id === contentId) {
        const updatedContent = { 
          ...get().currentContent,
          comments: [...(get().currentContent.comments || []), response.data.comment]
        };
        set({ currentContent: updatedContent });
      }
      
      set({ isLoading: false });
      return response.data.comment;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  fetchBrandContent: async (brandId, params = {}) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.getBrandContent(brandId, params);
      set({ isLoading: false });
      return response.data.content;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  fetchCampaignContent: async (campaignId, params = {}) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.getCampaignContent(campaignId, params);
      set({ isLoading: false });
      return response.data.content;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  addContentReference: async (contentId, referenceData) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.addContentReference(contentId, referenceData);
      
      // Update current content if needed
      if (get().currentContent?._id === contentId) {
        const updatedContent = { 
          ...get().currentContent,
          references: [...(get().currentContent?.references || []), response?.data?.reference]
        };
        set({ currentContent: updatedContent });
      }
      
      set({ isLoading: false });
      return response?.data?.reference;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  removeContentReference: async (contentId, referenceId) => {
    try {
      set({ isLoading: true, operationError: null });
      await contentService.removeContentReference(contentId, referenceId);
      
      // Update current content if needed
      if (get().currentContent?._id === contentId) {
        const updatedContent = { 
          ...get().currentContent,
          references: (get().currentContent.references || [])
            .filter(ref => ref._id !== referenceId)
        };
        set({ currentContent: updatedContent });
      }
      
      set({ isLoading: false });
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  getContentVersions: async (contentId) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.getContentVersions(contentId);
      set({ isLoading: false });
      return response.data.versions;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  revertToVersion: async (contentId, versionNumber) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.revertToVersion(contentId, versionNumber);
      
      // Update current content if it's the one being reverted
      if (get().currentContent?._id === contentId) {
        set({ currentContent: response.data.content });
      }
      
      set({ isLoading: false });
      return response.data.content;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  },
  
  searchContent: async (query) => {
    try {
      set({ isLoading: true, operationError: null });
      const response = await contentService.searchContent(query);
      set({ isLoading: false });
      return response.data.content;
    } catch (error) {
      set({ 
        isLoading: false, 
        operationError: error.response?.data?.message || error.message
      });
      throw error;
    }
  }
}));

export default useContentStore;